// API client: token handling, JSON fetch wrapper, warning-override support.

let token: string | null = localStorage.getItem('tpa_token');
export let currentUser: { username: string; display_name: string; role: string } | null = null;

export function setSession(t: string | null, user: any) {
  token = t;
  currentUser = user;
  if (t) localStorage.setItem('tpa_token', t);
  else localStorage.removeItem('tpa_token');
}

export class ApiError extends Error {
  status: number;
  errors: string[];
  warnings: string[];
  overridable: boolean;
  constructor(status: number, body: any) {
    super(body?.error || (body?.errors || []).map((e: any) => (typeof e === 'string' ? e : e.message)).join(' ') || `Request failed (${status})`);
    this.status = status;
    this.errors = (body?.errors || []).map((e: any) => (typeof e === 'string' ? e : `Row ${e.row} ${e.column}: ${e.message}`));
    this.warnings = body?.warnings || [];
    this.overridable = !!body?.overridable;
  }
}

export async function api(path: string, options: { method?: string; body?: any } = {}): Promise<any> {
  const res = await fetch('/api' + path, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined
  });
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    setSession(null, null);
    window.dispatchEvent(new Event('tpa-logout'));
  }
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = { error: text }; }
  if (!res.ok) throw new ApiError(res.status, body);
  return body;
}

// Save that surfaces overridable warnings: asks the user for a reason, then retries.
export async function saveWithOverride(path: string, method: string, body: any): Promise<any> {
  try {
    return await api(path, { method, body });
  } catch (e) {
    if (e instanceof ApiError && e.overridable) {
      const reason = window.prompt(
        'Warning:\n\n' + e.warnings.join('\n') +
        '\n\nTo save anyway, enter a reason (recorded in the audit history), or Cancel:');
      if (reason === null) throw new ApiError(0, { error: 'Cancelled.' });
      if (!reason.trim()) throw new ApiError(0, { error: 'A reason is required to override the warning.' });
      return await api(path, { method, body: { ...body, override_reason: reason.trim() } });
    }
    throw e;
  }
}

export async function download(path: string, filename?: string) {
  const res = await fetch('/api' + path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error('Download failed.');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const dispo = res.headers.get('Content-Disposition') || '';
  a.download = filename || dispo.match(/filename="?([^";]+)/)?.[1] || 'download';
  a.click();
  URL.revokeObjectURL(a.href);
}
